import { useState, useEffect } from 'react';

export type Currency = string;

const REGION_CURRENCY: Record<string, Currency> = {
  AE: 'AED', GB: 'GBP', UK: 'GBP', FR: 'EUR', DE: 'EUR', ES: 'EUR', IT: 'EUR', NL: 'EUR', BE: 'EUR', PT: 'EUR', IE: 'EUR', AT: 'EUR', FI: 'EUR', GR: 'EUR', CY: 'EUR', MT: 'EUR', LU: 'EUR', SI: 'EUR', SK: 'EUR', EE: 'EUR', LV: 'EUR', LT: 'EUR', HR: 'EUR',
  US: 'USD', CA: 'CAD', AU: 'AUD', NZ: 'NZD', CH: 'CHF', NO: 'NOK', SE: 'SEK', DK: 'DKK', PL: 'PLN', CZ: 'CZK', HU: 'HUF', RO: 'RON', BG: 'BGN', JP: 'JPY', SG: 'SGD', HK: 'HKD', IN: 'INR', SA: 'SAR', QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR', ZA: 'ZAR', MX: 'MXN', BR: 'BRL',
};

const TIMEZONE_CURRENCY: Record<string, Currency> = {
  'Europe/London': 'GBP',
  'Asia/Dubai': 'AED',
  'Asia/Muscat': 'OMR',
  'Asia/Qatar': 'QAR',
  'Asia/Kuwait': 'KWD',
  'Asia/Bahrain': 'BHD',
  'Asia/Riyadh': 'SAR',
  'Europe/Zurich': 'CHF',
  'Europe/Oslo': 'NOK',
  'Europe/Stockholm': 'SEK',
  'Europe/Copenhagen': 'DKK',
  'Europe/Warsaw': 'PLN',
  'Europe/Prague': 'CZK',
  'Europe/Budapest': 'HUF',
  'Europe/Bucharest': 'RON',
  'Europe/Sofia': 'BGN',
  'Asia/Tokyo': 'JPY',
  'Asia/Singapore': 'SGD',
  'Asia/Hong_Kong': 'HKD',
  'Asia/Kolkata': 'INR',
  'Australia/Sydney': 'AUD',
  'Pacific/Auckland': 'NZD',
};

const EUROPEAN_TIMEZONE_PREFIXES = [
  'Europe/',
  'Atlantic/Azores',
  'Atlantic/Canary',
  'Atlantic/Faroe',
  'Atlantic/Madeira',
];

export function useGeoCurrency(): {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  isEU: boolean;
} {
  const [currency, setCurrency] = useState<Currency>('USD');
  const [isEU, setIsEU] = useState(false);

  useEffect(() => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const locale = navigator.languages?.[0] || navigator.language || '';
    const region = (() => {
      try {
        return new Intl.Locale(locale).region?.toUpperCase();
      } catch {
        return locale.split('-')[1]?.toUpperCase();
      }
    })();
    const detectedEU = EUROPEAN_TIMEZONE_PREFIXES.some((prefix) => timezone.startsWith(prefix));
    const detectedCurrency = (region && REGION_CURRENCY[region]) || TIMEZONE_CURRENCY[timezone] || (detectedEU ? 'EUR' : 'USD');

    setCurrency(detectedCurrency);
    setIsEU(detectedCurrency === 'EUR');
  }, []);

  return { currency, setCurrency, isEU };
}
